import type { ProjectSection } from '@tomo/core';
import { ConfirmProvider, Toaster } from '@tomo/ui';
import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider } from 'react-router';
import { useMeta, useSession } from './data/hooks.tsx';
import { BoardsPage } from './features/boards/BoardsPage.tsx';
import { BrainstormRedirect } from './features/boards/BrainstormRedirect.tsx';
import { CollectionPage } from './features/collections/CollectionPage.tsx';
import { OverviewPage } from './features/overview/OverviewPage.tsx';
import { Launch } from './features/projects/Launch.tsx';
import { NewProjectPage } from './features/projects/NewProjectPage.tsx';
import { RecordPage } from './features/records/RecordPage.tsx';
import { NotFound, RouteError } from './features/shell/Errors.tsx';
import { ProjectRoot } from './features/shell/ProjectRoot.tsx';

/** Code-split a route: the module loads the first time the route is visited. */
const page =
  <M,>(load: () => Promise<M>, pick: (m: M) => ComponentType) =>
  async () => ({ Component: pick(await load()) });

/**
 * A section that was removed is gone from the app, so its addresses lead back to the overview.
 * Nothing in it was deleted: adding the section again makes these pages work as before.
 */
function SectionGuard({ section }: { section: ProjectSection }) {
  const session = useSession();
  const meta = useMeta();
  return meta.sections.includes(section) ? <Outlet /> : <Navigate to={`/p/${session.id}`} replace />;
}

const router = createBrowserRouter([
  { path: '/', element: <Launch />, errorElement: <RouteError /> },
  { path: '/new', element: <NewProjectPage />, errorElement: <RouteError /> },
  { path: '/design', lazy: page(() => import('./features/design/DesignSystemPage.tsx'), (m) => m.DesignSystemPage), errorElement: <RouteError /> },
  { path: '/settings', lazy: page(() => import('./features/settings/AppSettingsPage.tsx'), (m) => m.AppSettingsPage), errorElement: <RouteError /> },
  { path: '/invite/:token', lazy: page(() => import('./features/account/InvitePage.tsx'), (m) => m.InvitePage), errorElement: <RouteError /> },
  // Links from Tomo's emails.
  { path: '/reset-password/:token', lazy: page(() => import('./features/account/ResetPasswordPage.tsx'), (m) => m.ResetPasswordPage), errorElement: <RouteError /> },
  { path: '/verify-email/:token', lazy: page(() => import('./features/account/VerifyEmailPage.tsx'), (m) => m.VerifyEmailPage), errorElement: <RouteError /> },
  {
    path: '/p/:projectId',
    element: <ProjectRoot />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <OverviewPage /> },
      { path: 'c/:collectionId', element: <CollectionPage /> },
      { path: 'r/:recordId', element: <RecordPage /> },
      {
        element: <SectionGuard section="boards" />,
        children: [
          { path: 'boards', element: <BoardsPage /> },
          { path: 'boards/:boardId', lazy: page(() => import('./features/boards/CanvasPage.tsx'), (m) => m.CanvasPage) },
        ],
      },
      // Boards used to live under /brainstorm; links and bookmarks from then still work.
      { path: 'brainstorm', element: <BrainstormRedirect /> },
      { path: 'brainstorm/:boardId', element: <BrainstormRedirect /> },
      {
        element: <SectionGuard section="votes" />,
        children: [{ path: 'votes', lazy: page(() => import('./features/polls/PollsPage.tsx'), (m) => m.PollsPage) }],
      },
      { path: 'trash', lazy: page(() => import('./features/trash/TrashPage.tsx'), (m) => m.TrashPage) },
      { path: 'settings', lazy: page(() => import('./features/settings/ProjectSettingsPage.tsx'), (m) => m.ProjectSettingsPage) },
    ],
  },
  { path: '*', element: <NotFound /> },
]);

export function App() {
  return (
    <ConfirmProvider>
      <RouterProvider router={router} />
      <Toaster />
    </ConfirmProvider>
  );
}
